import classNames from 'classnames';
import React, { useEffect, useMemo, useState } from 'react';
import type { SetNonNullable } from 'type-fest';
import type { EditVersionChange } from './calculateVersionChanges';
import * as styles from './EditFacesMagneticWall.module.scss';
import {
  getVersionChangeContentRects,
  useVersionChanges,
  useVersionChangesFocus,
} from './VersionChanges';
import { useVersionColorMode } from './VersionColorMode';
import type { VersionChangesColorMode } from './VersionColorMode';
import Avatar from '../../components/Avatar';
import Button from '../../components/Button';
import Tooltip from '../../components/Tooltip';
import Header from '../../editor/formats/Header';
import {
  getUserSequence,
  USER_SEQUENCE_ATTRIBUTE,
} from '../../editor/formats/UserSequence';
import { useActiveGutterBlot } from '../../editor/Gutter';
import type { HoveredBlotListenerSetting } from '../../editor/HoveredBlotListener';
import { useHoveredBlotListener } from '../../editor/HoveredBlotListener';
import { useQuill } from '../../editor/QuillContext';
import { useGetUser } from '../../user/useAllUsers';
import MagneticWallLayout, {
  useMagneticWallLayout,
} from '../MagneticWallLayout';
import type { RelativeDOMRect } from '../utils/toRelativeDOMRect';
import toRelativeDOMRect from '../utils/toRelativeDOMRect';
import useLayoutSize, { LayoutSize } from '../utils/useLayoutSize';

const AVATAR_SIZE = parseInt(styles.avatarSize, 10);
const SPACING = parseInt(styles.spacing, 10);
const SPACING_DIFF_MODE = parseInt(styles.spacingDiffMode, 10);

interface EditFaceProps {
  versionChange: EditVersionChange;
  isInitiallyShowingTooltip?: boolean;
  onClick: () => void;
  focused: boolean;
  colorMode: VersionChangesColorMode;
}

// eslint-disable-next-line import/no-unused-modules -- exported for testing
export const EditFace = ({
  versionChange,
  isInitiallyShowingTooltip,
  onClick,
  focused,
  colorMode,
}: EditFaceProps) => {
  const getUser = useGetUser();
  const author = getUser({ id: versionChange.authorId });

  return (
    <div className={styles.editFaceContainer}>
      <Tooltip
        open={isInitiallyShowingTooltip}
        asChild
        content={
          <div className={styles.tooltipContainer}>
            <div className={styles.tooltipName}>{author.name}</div>
            <Tooltip.Muted className={styles.tooltipTitle}>
              {author.title}
            </Tooltip.Muted>
          </div>
        }
      >
        <div
          {...(colorMode === 'collaborators'
            ? {
                [USER_SEQUENCE_ATTRIBUTE]: getUserSequence(
                  versionChange.authorId,
                ),
              }
            : {})}
          className={styles.avatarButtonContainer}
        >
          <Button
            className={classNames(styles.avatarButton, {
              [styles.hasRing]: focused || colorMode === 'collaborators',
            })}
            aria-label={author.name}
            onClick={onClick}
          >
            <Avatar size={AVATAR_SIZE} user={author} />
          </Button>
        </div>
      </Tooltip>
    </div>
  );
};

interface InnerProps {
  versionChanges: EditVersionChange[];
  hoveredBlotRect: RelativeDOMRect | null;
  colorMode: VersionChangesColorMode;
}

const Inner = ({ versionChanges, hoveredBlotRect, colorMode }: InnerProps) => {
  const { layout, debouncedUpdateDesiredPositions, updateThreadHeights } =
    useMagneticWallLayout();
  const { focusedVersionChange, focusById } = useVersionChangesFocus();

  const visibleItems = useMemo(
    () =>
      versionChanges
        .map((item) => {
          const isOverlapped = layout.overlappedIds.has(item.id);
          const top = layout.tops[item.id];

          if (top != null && hoveredBlotRect != null) {
            const bottom = top + AVATAR_SIZE;

            const isBelow = top > hoveredBlotRect.bottom;
            const isAbove = bottom < hoveredBlotRect.top;

            const isIntersectingAvatar = !isBelow && !isAbove;

            return {
              ...item,
              top,
              isInBackground: isIntersectingAvatar,
              isOverlapped,
            };
          }

          return {
            ...item,
            top,
            isInBackground: false,
            isOverlapped,
          };
        })
        .filter(
          (item): item is SetNonNullable<typeof item, 'top'> =>
            item.top != null,
        ),
    [layout.overlappedIds, layout.tops, versionChanges, hoveredBlotRect],
  );

  useEffect(() => {
    debouncedUpdateDesiredPositions();
    updateThreadHeights();
  }, [visibleItems, debouncedUpdateDesiredPositions, updateThreadHeights]);

  return (
    <>
      {visibleItems.map((versionChange) => {
        const isFocused = versionChange.id === focusedVersionChange?.id;

        return (
          <MagneticWallLayout.Item
            key={versionChange.id}
            id={versionChange.id}
            focused={isFocused}
            top={versionChange.top}
            className={classNames({
              [styles.isInBackground]: versionChange.isInBackground,
            })}
          >
            <EditFace
              versionChange={versionChange}
              onClick={() => focusById(versionChange.id)}
              focused={isFocused}
              colorMode={colorMode}
            />
          </MagneticWallLayout.Item>
        );
      })}
    </>
  );
};

const hoveredBlotListenerSetting: HoveredBlotListenerSetting<
  Record<string, unknown>,
  Header
> = {
  isHoveredBlot(blot) {
    return blot instanceof Header;
  },
};

const EditFacesMagneticWall = () => {
  const activeGutterBlot = useActiveGutterBlot();
  const { versionChanges } = useVersionChanges();
  const { focusedVersionChange } = useVersionChangesFocus();
  const colorMode = useVersionColorMode();

  const editVersionChanges = useMemo(
    () => versionChanges.filter((v) => v.type === 'edit'),
    [versionChanges],
  );

  const { quill } = useQuill();

  const [hoveredBlotRect, setHoveredBlotRect] =
    useState<RelativeDOMRect | null>(null);

  const { hoveredBlot: hoveredHeader } = useHoveredBlotListener(
    quill,
    hoveredBlotListenerSetting,
  );

  const hoveredBlot = activeGutterBlot ?? hoveredHeader;

  useEffect(() => {
    if (!quill) return;

    if (hoveredBlot) {
      setHoveredBlotRect(
        toRelativeDOMRect(
          hoveredBlot.domNode.getBoundingClientRect(),
          quill.container.getBoundingClientRect(),
        ),
      );
    } else {
      setHoveredBlotRect(null);
    }
  }, [hoveredBlot, quill]);

  const layoutSize = useLayoutSize();

  if (
    layoutSize &&
    [LayoutSize.mobile, LayoutSize.tablet].includes(layoutSize)
  ) {
    return null;
  }

  return (
    <div
      className={classNames(styles.container, {
        [styles.hasHoveredBlot]: hoveredBlot != null,
      })}
    >
      <MagneticWallLayout.Root
        itemSize="gutter"
        magneticWallItems={editVersionChanges}
        focus={focusedVersionChange}
        getRelativeContentRects={() =>
          quill ? getVersionChangeContentRects(quill) : []
        }
        spacing={colorMode === 'diff' ? SPACING_DIFF_MODE : SPACING}
        minItemHeight={AVATAR_SIZE}
      >
        <Inner
          versionChanges={editVersionChanges}
          hoveredBlotRect={hoveredBlotRect}
          colorMode={colorMode}
        />
      </MagneticWallLayout.Root>
    </div>
  );
};

export default EditFacesMagneticWall;
